/**
 * Initialize state sets all application state at once based on api/identity/info.
 * It should be run EITHER in the top scope of app.vue on the server only,
 * OR after a successful login in the client only, but not both.
 */
export default function useInitializeState() {
    const { $api } = useNuxtApp();
    const { locale } = useI18n();
    const isAuthenticated = useStateIsAuthenticated();
    const userInfo = useStateUserInfo();
    const locationInfo = useStateLocationInfo();
    const regionInfo = useStateRegionInfo();
    const currencyInfo = useStateCurrencyInfo();
    const globalConfig = useStateGlobalConfig();
    const config = useRuntimeConfig();
    const { cdnUrl } = config.public;

    // Determine if the connection is secure, respecting x-forwarded-proto from the load balancer
    const requestUrl = useRequestURL();
    const isSecure = requestUrl.protocol === "https:";

    // ASP.NET Core makes use of 2 tokens for XSRF prevention
    // the persistent cookie token "eciarvt" and the request token "XSRF-TOKEN"
    const eciarvtCookie = useCookie("eciarvt", { httpOnly: false, sameSite: "strict", path: "/", secure: isSecure });
    const xsrfCookie = useCookie("XSRF-TOKEN", { httpOnly: false, sameSite: "strict", path: "/", secure: isSecure });
    const xsrfToken = useState("xsrfToken", () => xsrfCookie);

    return async function initializeState() {
        const accountData = await $api<AccountData>("identity/info");

        if (accountData.XSRFCookieToken) {
            eciarvtCookie.value = accountData.XSRFCookieToken;
        }

        xsrfCookie.value = accountData.XSRFRequestToken;
        xsrfToken.value = accountData.XSRFRequestToken;
        isAuthenticated.value = accountData.IsAuthenticated ?? false;
        userInfo.value = accountData.User;
        locationInfo.value = accountData.Location;

        globalConfig.value = {
            isAuthenticated: isAuthenticated.value,
            CDNUrl: cdnUrl,
            CurrencyCode: accountData.Location.CurrencyCode ?? "USD",
            LanguageCode: locale.value,
            Features: accountData.Features,
            SearchText: "",
            ShowTariffWarning: accountData.ShowTariffWarning ?? accountData.Location.CountryCode === "US",
        };
        regionInfo.value = accountData.Regions || [];
        currencyInfo.value = accountData.Currencies || [];

        return { isAuthenticated, userInfo, locationInfo, regionInfo, currencyInfo, globalConfig, accountData };
    };
}
